import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from 'redux';
import styled from '@emotion/styled';

import { breakpoints, colors, durations, Icon } from '../../../design-system';
import { RootState } from '../../../store/rootReducer';
import { getDataTicket, getOrders } from '../actions';
import LoadingOrders from './LoadingOrders';
import { ERR_GENERIC } from '../../common/locale';
import { formatPrice } from '../../common/utils';
import locale, { ORDER_STATUS, SHIPPING_TYPES } from '../locale';
import { getTrackingNumbers } from '../utils';
import paths from '../../routing/paths';
import accountPaths from '../paths';
import { Order, OrderStatus } from '../types';
import { ShippingTypes } from '../../cart/types';

const StyledOrdersContainer = styled.div`
  width: 100%;
  justify-self: center;
  .orders-title {
    margin: 0;
    font-family: 'Ogg-text';
    font-size: 2.4rem;
    font-weight: 700;
    color: ${colors.BLACK};

    @media (min-width: ${breakpoints.S}px) {
      margin-bottom: 24px;
    }
  }
  .orders-error-message {
    font-size: 1.4rem;
    font-weight: 400;
    color: ${colors.MAIN_ERROR};
  }
  .order-total-header,
  .order-shipping-type-header,
  .order-status-header,
  .order-tracking-number-arrow-header,
  .order-method-header,
  .order-tracking-number-arrow,
  .order-total,
  .order-total-refunded,
  .order-shipping-type,
  .order-status,
  .order-method {
    display: none;
    @media (min-width: ${breakpoints.M}px) {
      display: table-cell;
    }
  }
  .order-tracking-number {
    display: none;
    @media (min-width: ${breakpoints.S}px) {
      display: block;
    }
  }
  .order-tracking-number-mobile {
    display: block;
    @media (min-width: ${breakpoints.S}px) {
      display: none;
    }
  }
  .order-total-refunded {
    color: ${colors.MAIN_SUCCESS};
  }
`;

const StyledTable = styled.table`
  max-width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0 16px;
`;

const TableHeader = styled.thead`
  height: 64px;
  font-size: 1.4rem;
  color: ${colors.BLACK};
`;

const TableRow = styled.tr`
  height: 64px;
  background: ${colors.WHITE};
  box-shadow: 0 0 0 1px ${colors.LIGHT};
  transition: background ${durations.FOCUS_DELAY}ms ease-in-out;
  overflow: hidden;
  border-radius: 8px;

  @media (hover: hover) {
    &:hover {
      background: ${colors.LIGHT};
      cursor: pointer;
    }
  }
`;

const TableCell = styled.td`
  font-size: 1.4rem;
  padding: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: calc((100vw - 32px) / 3);
  max-width: calc((100vw - 32px) / 3);

  &:first-of-type {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:nth-of-type(7) {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  @media (min-width: ${breakpoints.M}px) {
    &:nth-of-type(7) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    &:last-of-type {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
  }

  @media (min-width: ${breakpoints.S}px) {
    &:not(.order-number, .order-number-header) {
      width: 120px;
    }
  }
  @media (min-width: ${breakpoints.M}px) {
    &:not(.order-number, .order-number-header) {
      width: 150px;
    }
  }
  .order-status-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .order-status-returned {
    color: ${colors.MAIN_SUCCESS};
  }
`;

const TableHeaderCell = styled.th`
  padding: 12px;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: calc((100vw - 32px) / 3);
  max-width: calc((100vw - 32px) / 3);
  @media (min-width: ${breakpoints.S}px) {
    &:not(.order-number, .order-number-header) {
      width: 150px;
    }
  }
  @media (min-width: ${breakpoints.M}px) {
    &:not(.order-number, .order-number-header) {
      width: 150px;
    }
  }
`;

TableHeaderCell.defaultProps = {
  scope: 'col',
};

export const Orders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getOrders() as unknown as UnknownAction);
    dispatch(getDataTicket() as unknown as UnknownAction);
  }, []);

  const { orders, dataTicket, isFetching, dataTicketIsFetching, errMsg } = useSelector(
    (state: RootState) => state.account
  );

  const { errorType } = dataTicket ?? {};

  const handleOrderClick = ({
    orderNumber,
    migrated,
    ticketId,
    ticketDate,
  }: {
    orderNumber: number;
    migrated: boolean;
    ticketId?: number;
    ticketDate?: string;
  }) => {
    if (ticketId && ticketDate) {
      navigate(`${paths.ACCOUNT}${accountPaths.ORDERS_OFFLINE}/${ticketId}/${ticketDate}`);
    } else {
      navigate(`${orderNumber}${migrated ? '?migrated=1' : ''}`);
    }
  };

  const data: Order[] = [];
  dataTicket?.tickets.forEach((ticket) => {
    data.push({
      id: ticket.id,
      createdAt: ticket.orderDate,
      migrated: false,
      orderNumber: ticket.posNumber,
      shippingType: ShippingTypes.OFFLINE,
      status: OrderStatus.COLLECTED,
      total: ticket.total,
      tracking: [],
      trackingNumber: '',
    });
  });

  const sortedOrders = useMemo(() => {
    return [...(orders || []), ...(data || [])].sort((a, b) => {
      const dateDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (dateDiff !== 0) {
        return dateDiff;
      }
      return Number(b.orderNumber) - Number(a.orderNumber);
    });
  }, [orders, data]);

  return (
    <StyledOrdersContainer>
      <h2 className="orders-title">Vos commandes</h2>
      {isFetching || dataTicketIsFetching ? (
        <LoadingOrders />
      ) : errMsg || errorType ? (
        <p className="orders-error-message">{ERR_GENERIC}</p>
      ) : (
        <StyledTable>
          <TableHeader>
            <tr>
              <TableHeaderCell aria-sort="descending">Date</TableHeaderCell>
              <TableHeaderCell>N° commande</TableHeaderCell>
              <TableHeaderCell className="order-total-header">Montant</TableHeaderCell>
              <TableHeaderCell className="order-shipping-type-header">Livraison</TableHeaderCell>
              <TableHeaderCell className="order-status-header">Statut</TableHeaderCell>
              <TableHeaderCell className="order-method-header">{`Mode d'achat`}</TableHeaderCell>
              <TableHeaderCell className="order-number-header">N° de suivi</TableHeaderCell>
              <TableHeaderCell className="order-tracking-number-arrow-header" />
            </tr>
          </TableHeader>
          <tbody>
            {sortedOrders?.map(
              ({
                id,
                orderNumber,
                createdAt,
                total,
                shippingType,
                status,
                tracking,
                trackingNumber,
                migrated,
              }) => {
                const trackingNumbers = getTrackingNumbers({
                  tracking: tracking?.map((item) => ({ trackingNumber: item })),
                  legacyNumber: trackingNumber,
                });

                return (
                  <TableRow
                    role="button"
                    tabIndex={0}
                    key={orderNumber}
                    data-testid={`table-row-order-${orderNumber}-as-button-to-order-details`}
                    aria-label={`Bouton pour voir les détails de votre commande n° ${orderNumber}`}
                    onClick={() =>
                      handleOrderClick({
                        orderNumber,
                        migrated,
                        ticketId: shippingType === ShippingTypes.OFFLINE ? id : undefined,
                        ticketDate: shippingType === ShippingTypes.OFFLINE ? createdAt : undefined,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleOrderClick({
                          orderNumber,
                          migrated,
                          ticketId: shippingType === ShippingTypes.OFFLINE ? id : undefined,
                          ticketDate:
                            shippingType === ShippingTypes.OFFLINE ? createdAt : undefined,
                        });
                      }
                    }}
                  >
                    <TableCell aria-label={`Date de la commande n° ${orderNumber}`}>
                      {new Date(createdAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell aria-label="N° de la commande">{orderNumber}</TableCell>
                    <TableCell
                      aria-label={`Montant de la commande n° ${orderNumber}`}
                      className={total <= 0 ? 'order-total-refunded' : 'order-total'}
                    >
                      {formatPrice(Math.abs(total))}
                    </TableCell>
                    <TableCell
                      aria-label={`Livraison de la commande n° ${orderNumber}`}
                      className="order-shipping-type"
                    >
                      {shippingType && SHIPPING_TYPES[shippingType]}
                    </TableCell>
                    <TableCell
                      aria-label={`Statut de la commande n° ${orderNumber}`}
                      className="order-status"
                    >
                      <div className="order-status-container">
                        <span>{ORDER_STATUS[status]}</span>
                        {total <= 0 && <span className="order-status-returned">Retournée</span>}
                      </div>
                    </TableCell>
                    <TableCell
                      aria-label={`Mode d'achat de la commande n° ${orderNumber}`}
                      className="order-method"
                    >
                      {SHIPPING_TYPES[shippingType === 'OFFLINE' ? 'STORE' : 'INLINE']}
                    </TableCell>
                    <TableCell className="order-number">
                      {trackingNumbers.length > 1 ? (
                        <button
                          className="anchor-animated"
                          data-testid="btn-order-tracking-view-more"
                          aria-label={`Bouton pour voir les détails de votre commande n° ${orderNumber}`}
                          id="link-order-tracking-view-more"
                          type="button"
                          onClick={() =>
                            handleOrderClick({
                              orderNumber,
                              migrated,
                            })
                          }
                        >
                          {locale.VIEW_MORE}
                        </button>
                      ) : trackingNumbers.length > 0 ? (
                        <Link
                          id="link-order-tracking"
                          className="anchor-animated"
                          data-testid={`link-to-order-tracking-${trackingNumbers[0]}`}
                          to={`${paths.ACCOUNT}${accountPaths.TRACKING}/${trackingNumbers[0]}`}
                          aria-label={`Lien pour voir le suivi de votre commande n° ${orderNumber}`}
                        >
                          <span className="order-tracking-number">{trackingNumbers[0]}</span>
                          <span className="order-tracking-number-mobile">{locale.VIEW_MORE}</span>
                        </Link>
                      ) : shippingType === ShippingTypes.OFFLINE ? (
                        <button tabIndex={0} type="button" className="anchor-animated">
                          {locale.VIEW_MORE}
                        </button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="order-tracking-number-arrow">
                      <Icon name="chevronRight" />
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </tbody>
        </StyledTable>
      )}
    </StyledOrdersContainer>
  );
};
